import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { useAuth } from '../contexts/AuthContext';
import { FaUser, FaLock } from 'react-icons/fa';

// Illustration SVG pour la colonne gauche
const SchoolManagementSVG = () => (
  <svg viewBox="0 0 250 250" width="210" height="210" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="20" y="120" width="210" height="90" rx="12" fill="#4F8CD6"/>
    <rect x="40" y="135" width="170" height="45" rx="7" fill="#F5F7FA"/>
    <rect x="80" y="80" width="90" height="30" rx="7" fill="#F5F7FA" stroke="#4F8CD6"/>
    <circle cx="125" cy="60" r="24" fill="#F5F7FA" stroke="#4F8CD6" strokeWidth="3"/>
    <rect x="105" y="145" width="40" height="20" rx="3" fill="#E2E8F0"/>
    <rect x="50" y="185" width="50" height="10" rx="3" fill="#DFDFDF"/>
    <rect x="150" y="185" width="50" height="10" rx="3" fill="#DFDFDF"/>
    <path d="M70 135v-25c0-5 3-9 7-9h96c4 0 7 4 7 9v25" fill="#4F8CD6" opacity=".18"/>
    <text x="125" y="52" textAnchor="middle" fontSize="13" fill="#4F8CD6">Scolarité</text>
    <text x="125" y="64" textAnchor="middle" fontSize="9" fill="#99BCE2">Gestion</text>
    <text x="125" y="165" textAnchor="middle" fontSize="9" fill="#4F8CD6">Salle | Élèves | Suivi</text>
  </svg>
);

const Login = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated, loading } = useAuth();
  const [formLoading, setFormLoading] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/app/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/app/dashboard" replace />;

  const onSubmit = async (data) => {
    setFormLoading(true);
    const result = await login(data);
    setFormLoading(false);
    if (result.success) {
      toast.success('Connexion réussie!');
      navigate('/app/dashboard', { replace: true });
    } else {
      toast.error(result.error);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-200">
      <div
        className="flex w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden border border-gray-100"
        style={{ minHeight: 500 }}
      >
        {/* Left: Illustration image */}
        <div className="hidden md:flex w-1/2 items-center justify-center bg-gray-100">
          <img
            src="/images/schola2.png"
            alt="Illustration gestion scolaire Schola"
            className="object-contain  shadow-md"
            style={{ maxHeight: '100%', width: '100%' }}
            onError={e => e.target.style.opacity = 0}
          />
        </div>
        {/* Right: Form */}
        <div className="flex w-full md:w-1/2 justify-center items-center bg-white p-6">
          <div className="max-w-sm w-full space-y-8 mx-auto">
            {/* Logo sans déformation */}
            <div className="flex justify-center">
              <img
                src={process.env.PUBLIC_URL + '/logo_schola.jpg'}
                alt="Logo Schola"
                className="mx-auto mb-2 animate-bounce"
                style={{ height: 80, width: 230, maxWidth: 250, animationDuration: '1.4s', borderRadius: 16 }}
              />
            </div>
            {/* Texte de bienvenue */}
            <div className="text-center mb-6">
              <div className="text-2xl font-bold text-gray-900 leading-tight">
                Bienvenue&nbsp;à&nbsp;Schola
              </div>
              <div className="mt-1 text-base text-primary-600">
                Gestion&nbsp;d'établissements&nbsp;scolaires
              </div>
            </div>
            {/* Formulaire modernisé */}
            <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-4">
                {/* Utilisateur */}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <FaUser size={18} />
                  </span>
                  <input
                    {...register('username', { required: 'Nom d\'utilisateur requis' })}
                    type="text"
                    autoComplete="username"
                    className="pl-10 pr-3 py-2 w-full rounded-lg bg-gray-50 border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 text-gray-900 shadow-sm transition placeholder-gray-400 sm:text-sm"
                    placeholder="Nom d'utilisateur"
                  />
                  {errors.username && (
                    <p className="mt-1 text-xs text-red-600">{errors.username.message}</p>
                  )}
                </div>
                {/* Mot de passe */}
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                    <FaLock size={18} />
                  </span>
                  <input
                    {...register('password', { required: 'Mot de passe requis' })}
                    type="password"
                    autoComplete="current-password"
                    className="pl-10 pr-3 py-2 w-full rounded-lg bg-gray-50 border border-gray-200 focus:border-primary-500 focus:ring-2 focus:ring-primary-200 text-gray-900 shadow-sm transition placeholder-gray-400 sm:text-sm"
                    placeholder="Mot de passe"
                  />
                  {errors.password && (
                    <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
                  )}
                </div>
              </div>
              <div>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="group relative w-full flex justify-center py-2 px-4 text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition"
                >
                  {formLoading ? 'Connexion...' : 'Se connecter'}
                </button>
              </div>
            </form>
            {/* Copyright du footer */}
            <div className="mt-8 flex flex-col items-center justify-center">
              <img
                src={process.env.PUBLIC_URL + '/tpsoft_copyright.jpg'}
                alt="TPSoft Logo Copyright"
                style={{ height: 48, width: 'auto', maxWidth: 120, borderRadius: 8, objectFit: 'contain' }}
                className="mb-2 opacity-80"
                onError={e => e.target.style.opacity = 0}
              />
              <span className="text-xs text-gray-500 text-center">
                TPSoft Informatique - Juin 2025 - Tous droits réservés.
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
